import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { User, Shield, Moon, Sun, Upload, CheckCircle, Loader2, Lock, Save, ImageIcon, Palette } from 'lucide-react';

export default function SettingsPage() {
  const { user, login } = useAuth();
  const [darkMode, setDarkMode] = useState(document.documentElement.classList.contains('dark'));
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoData, setLogoData] = useState(null);
  const [brandColors, setBrandColors] = useState({ primary: '#0E4F8F', accent: '#F97316' });
  const [savingColors, setSavingColors] = useState(false);
  const logoInputRef = useRef(null);
  const [credentials, setCredentials] = useState({ currentPassword: '', newEmail: '', newPassword: '', newName: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.company_id) {
      fetchLogo();
    }
  }, [user]);

  const fetchLogo = async () => {
    try {
      const res = await api.get('/builder/agency/logo');
      setLogoData(res.data);
    } catch {}
  };

  const fetchBranding = async () => {
    try {
      const res = await api.get('/builder/agency/branding');
      const colors = res.data?.branding?.brand_colors;
      if (colors) setBrandColors({ primary: colors.primary || '#0E4F8F', accent: colors.accent || '#F97316' });
    } catch {}
  };

  useEffect(() => {
    if (user?.company_id) { fetchBranding(); }
  }, [user]);

  const toggleDarkMode = () => {
    document.documentElement.classList.toggle('dark');
    setDarkMode(!darkMode);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(png|jpg|jpeg|webp)$/i.test(file.name)) {
      toast.error('Only PNG, JPG, or WebP images accepted');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/builder/agency/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Agency logo uploaded!');
      setLogoData({ logo_url: res.data.logo_url, filename: res.data.filename });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleSaveBrandColors = async () => {
    setSavingColors(true);
    try {
      await api.put('/builder/agency/branding', { brand_colors: brandColors });
      toast.success('Brand colors saved! PDF exports will use these colors.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save colors');
    } finally { setSavingColors(false); }
  };

  const handleChangeCredentials = async () => {
    if (!credentials.currentPassword) {
      toast.error('Current password is required');
      return;
    }
    if (!credentials.newEmail && !credentials.newPassword && !credentials.newName) {
      toast.error('Provide at least one field to update');
      return;
    }
    if (credentials.newPassword && credentials.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      const payload = { current_password: credentials.currentPassword };
      if (credentials.newEmail) payload.new_email = credentials.newEmail;
      if (credentials.newPassword) payload.new_password = credentials.newPassword;
      if (credentials.newName) payload.new_name = credentials.newName;
      await api.put('/auth/change-credentials', payload);
      toast.success('Credentials updated successfully. Please re-login if you changed email or password.');
      setCredentials({ currentPassword: '', newEmail: '', newPassword: '', newName: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update credentials');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Manage your account, branding, and preferences</p>
      </div>

      {/* Profile */}
      <Card className="border border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <User className="w-4 h-4 text-primary" /> Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-sm text-muted-foreground">Name</Label><p className="font-medium mt-0.5">{user?.name}</p></div>
            <div><Label className="text-sm text-muted-foreground">Email</Label><p className="font-medium mt-0.5">{user?.email}</p></div>
            <div><Label className="text-sm text-muted-foreground">Role</Label><p className="font-medium mt-0.5 capitalize">{user?.role?.replace('_', ' ')}</p></div>
            <div><Label className="text-sm text-muted-foreground">Department</Label><p className="font-medium mt-0.5">{user?.department || 'N/A'}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Change Credentials */}
      <Card className="border border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Lock className="w-4 h-4 text-primary" /> Change Credentials
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">Update your login email, password, or display name. Current password is required for verification.</p>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-sm">Current Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={credentials.currentPassword}
                onChange={e => setCredentials({...credentials, currentPassword: e.target.value})}
                placeholder="Enter current password" className="h-9" data-testid="current-password-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">New Name</Label>
                <Input value={credentials.newName}
                  onChange={e => setCredentials({...credentials, newName: e.target.value})}
                  placeholder={user?.name || 'Display name'} className="h-9" data-testid="new-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">New Email</Label>
                <Input type="email" value={credentials.newEmail}
                  onChange={e => setCredentials({...credentials, newEmail: e.target.value})}
                  placeholder={user?.email || 'New email'} className="h-9" data-testid="new-email-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">New Password</Label>
              <Input type="password" value={credentials.newPassword}
                onChange={e => setCredentials({...credentials, newPassword: e.target.value})}
                placeholder="Min 6 characters (leave blank to keep current)" className="h-9" data-testid="new-password-input" />
            </div>
            <Button onClick={handleChangeCredentials} disabled={saving} className="gap-2 bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="save-credentials-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Agency Logo */}
      {user?.company_id && (
        <Card className="border border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-primary" /> Agency Logo (PDF Branding)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload your agency logo (PNG/JPG). It will appear at the top of every page in exported PDF itineraries.
            </p>
            {logoData?.logo_url ? (
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 border border-border">
                <img src={logoData.logo_url} alt="Logo" className="h-12 max-w-[160px] object-contain rounded" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Logo uploaded</p>
                  <p className="text-xs text-muted-foreground">{logoData.filename}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} className="text-xs" data-testid="replace-logo-btn">
                  Replace
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-border rounded-xl">
                <ImageIcon className="w-8 h-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">No logo uploaded yet</p>
                <Button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                  className="gap-2 bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="upload-logo-btn">
                  {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload Logo (PNG/JPG)
                </Button>
              </div>
            )}
            <input type="file" ref={logoInputRef} accept=".png,.jpg,.jpeg,.webp" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
          </CardContent>
        </Card>
      )}

      {/* Brand Colors */}
      {user?.company_id && (
        <Card className="border border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Palette className="w-4 h-4 text-primary" /> Brand Colors (PDF Theme)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Customize the colors used in your PDF itinerary exports — headings, badges, table headers.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={brandColors.primary} onChange={e => setBrandColors({...brandColors, primary: e.target.value})}
                    className="w-10 h-10 rounded-lg border border-border cursor-pointer" data-testid="primary-color-picker" />
                  <Input value={brandColors.primary} onChange={e => setBrandColors({...brandColors, primary: e.target.value})} className="h-9 font-mono text-xs" />
                </div>
                <div className="h-2 rounded-full" style={{ background: brandColors.primary }} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Accent Color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={brandColors.accent} onChange={e => setBrandColors({...brandColors, accent: e.target.value})}
                    className="w-10 h-10 rounded-lg border border-border cursor-pointer" data-testid="accent-color-picker" />
                  <Input value={brandColors.accent} onChange={e => setBrandColors({...brandColors, accent: e.target.value})} className="h-9 font-mono text-xs" />
                </div>
                <div className="h-2 rounded-full" style={{ background: brandColors.accent }} />
              </div>
            </div>
            {/* Preview */}
            <div className="rounded-lg border border-border p-4" style={{ borderTopWidth: '3px', borderTopColor: brandColors.primary }}>
              <p className="text-xs text-muted-foreground mb-1">Preview</p>
              <h3 className="font-bold text-sm" style={{ color: brandColors.primary }}>Enchanting Bali Discovery</h3>
              <p className="text-xs mt-1" style={{ color: brandColors.accent }}>5 Nights / 6 Days</p>
            </div>
            <Button onClick={handleSaveBrandColors} disabled={savingColors} className="gap-2 bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="save-brand-colors-btn">
              {savingColors ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Brand Colors
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card className="border border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            {darkMode ? <Moon className="w-4 h-4 text-primary" /> : <Sun className="w-4 h-4 text-primary" />} Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Dark Mode</p>
              <p className="text-xs text-muted-foreground">Switch between light and dark themes</p>
            </div>
            <Switch checked={darkMode} onCheckedChange={toggleDarkMode} data-testid="dark-mode-switch" />
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="border border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" /> Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div><Label className="text-sm text-muted-foreground">User ID</Label><p className="font-mono text-xs mt-0.5">{user?.id}</p></div>
          {user?.company_id && <div><Label className="text-sm text-muted-foreground">Company ID</Label><p className="font-mono text-xs mt-0.5">{user?.company_id}</p></div>}
          <div><Label className="text-sm text-muted-foreground">Account Status</Label><p className="text-sm font-medium text-emerald-600 mt-0.5">Active</p></div>
          <div><Label className="text-sm text-muted-foreground">Session</Label><p className="text-xs text-muted-foreground mt-0.5">Auto-logout after 24 hours of inactivity</p></div>
        </CardContent>
      </Card>
    </div>
  );
}
